import axios from 'axios';
import { store } from '../store/store';
import { Logout, loginConfirmedAction } from '../store/actions/AuthActions';
import { applyRenewedToken, runLogoutTimer } from './AuthService';
import LocalStorageType from '../constants/LocalStorageType';

// Headers que o backend envia quando renova o token perto do vencimento (ver JwtAuthFilter).
const REFRESH_TOKEN_HEADER = 'x-refresh-token';
const REFRESH_TOKEN_EXPIRES_IN_HEADER = 'x-refresh-token-expires-in';

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    // Sem isso, uma conexão aceita pelo proxy mas nunca respondida pelo
    // backend (ex: durante um deploy) deixa a Promise pendurada para sempre,
    // travando o botão Salvar (formik.isSubmitting nunca volta a false).
    // Relatórios e consultas de dashboard podem processar muitos registros; 2 minutos
    // evita abortar a requisição antes de o backend concluir o cálculo.
    timeout: 120000,
});

axiosInstance.interceptors.request.use((config) => {
    const state = store.getState();
    let token = state.auth.auth.token;

    // O primeiro request após o login pode ocorrer antes de o Redux atualizar.
    // Nesse intervalo, usa a sessão persistida para não enviar uma chamada sem JWT.
    if (!token) {
        try {
            const userDetails = JSON.parse(
                localStorage.getItem(LocalStorageType.USER_DETAILS) || 'null',
            );
            token = userDetails?.token;
        } catch (error) {
            token = '';
        }
    }
    // config.params = config.params || {};
    // config.params['auth'] = token;
    
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

/**
 * Caso o token tenha expirado, o serviço retornará 401 e 
 * portanto a aplicação deverá redirecionar o usuário para a página de login.
 */
axiosInstance.interceptors.response.use(response => {
    // Renovação transparente da sessão: na última hora de vida do token o backend devolve um
    // token novo no header. Aplicando aqui, o operador que está trabalhando nunca é deslogado
    // no meio do expediente, sem precisar de tela de reautenticação.
    const renewedToken = response?.headers?.[REFRESH_TOKEN_HEADER];
    if (renewedToken) {
        const session = applyRenewedToken(
            renewedToken,
            response.headers[REFRESH_TOKEN_EXPIRES_IN_HEADER],
        );

        if (session) {
            store.dispatch(loginConfirmedAction(session));
            // Reprograma o logout automático para a nova validade. O runLogoutTimer cancela o
            // agendamento anterior, senão o timer antigo deslogaria na hora do token vencido.
            runLogoutTimer(
                store.dispatch,
                new Date(session.expireDate).getTime() - new Date().getTime(),
            );
        }
    }

    return response;
}, error => {
    // Caso o acesso não esteja permitido, a aplicação
    // redireciona o usuário para a página de login.
    if (error?.response?.status === 401) {
        // Realiza o logout e redireciona para a tela de login.
        store.dispatch(Logout());
        if (window.location.pathname !== '/login') {
            window.location.href = "/login";
        }
    }

    if (error?.response?.status === 423 && error?.response?.data?.code === 'SAAS_BLOCKED'
        && window.location.pathname !== '/saas-blocked') {
        sessionStorage.setItem('saas_blocked_details', JSON.stringify(error.response.data));
        window.location.href = '/saas-blocked';
    }

    return Promise.reject(error);
});

export default axiosInstance;
